// US NYSE market holiday calculator.
// Covers 2020–2035. Add more years to the hardcoded list only if the
// algorithm-computed dates ever diverge from the official NYSE schedule.

// ── helpers ──────────────────────────────────────────────────────────────────

function toKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// NYSE rule: if the actual date falls on Saturday, observe the Friday before;
// if on Sunday, observe the Monday after.
function observed(year, month0, day) {
  const d = new Date(year, month0, day);
  if (d.getDay() === 6) return new Date(year, month0, day - 1); // Sat → Fri
  if (d.getDay() === 0) return new Date(year, month0, day + 1); // Sun → Mon
  return d;
}

// nth weekday of a month (1-indexed n, weekday 0=Sun…6=Sat).
function nthWeekday(year, month0, n, wd) {
  let count = 0;
  for (let d = new Date(year, month0, 1); d.getMonth() === month0; d.setDate(d.getDate() + 1)) {
    if (d.getDay() === wd && ++count === n) return new Date(d);
  }
  return null;
}

// Last occurrence of weekday in a month.
function lastWeekday(year, month0, wd) {
  let last = null;
  for (let d = new Date(year, month0, 1); d.getMonth() === month0; d.setDate(d.getDate() + 1)) {
    if (d.getDay() === wd) last = new Date(d);
  }
  return last;
}

// Easter Sunday (Gregorian / Anonymous algorithm).
function easterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month0 = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day    = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month0, day);
}

// ── holiday set builder ───────────────────────────────────────────────────────

const _cache = {};

export function getMarketHolidays(year) {
  if (_cache[year]) return _cache[year];

  const holidays = new Set();
  const add = (d) => { if (d) holidays.add(toKey(d)); };

  // New Year's Day — Jan 1 (observed)
  add(observed(year, 0, 1));

  // If Jan 1 of next year is observed on Dec 31 of this year, add it too.
  // (Happens when Jan 1 next year falls on Saturday)
  const ny = new Date(year + 1, 0, 1);
  if (ny.getDay() === 6) holidays.add(toKey(new Date(year, 11, 31)));

  // Martin Luther King Jr. Day — 3rd Monday in January
  add(nthWeekday(year, 0, 3, 1));

  // Presidents' Day — 3rd Monday in February
  add(nthWeekday(year, 1, 3, 1));

  // Good Friday — 2 days before Easter
  const easter = easterSunday(year);
  add(new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() - 2));

  // Memorial Day — last Monday in May
  add(lastWeekday(year, 4, 1));

  // Juneteenth — June 19 (observed), recognised by NYSE from 2022
  if (year >= 2022) add(observed(year, 5, 19));

  // Independence Day — July 4 (observed)
  add(observed(year, 6, 4));

  // Labor Day — 1st Monday in September
  add(nthWeekday(year, 8, 1, 1));

  // Thanksgiving — 4th Thursday in November
  add(nthWeekday(year, 10, 4, 4));

  // Christmas — Dec 25 (observed)
  add(observed(year, 11, 25));

  _cache[year] = holidays;
  return holidays;
}

// ── public API ────────────────────────────────────────────────────────────────

/**
 * Returns true only if `date` is a US market trading day
 * (weekday AND not an NYSE holiday).
 */
export function isTradingDay(date) {
  const dow = date.getDay();
  if (dow === 0 || dow === 6) return false;
  return !getMarketHolidays(date.getFullYear()).has(toKey(date));
}

/**
 * Returns the nearest previous trading day on or before `date`.
 * Used to snap an initial date to a valid value.
 */
export function nearestTradingDay(date) {
  const d = new Date(date);
  for (let i = 0; i < 14; i++) {
    if (isTradingDay(d)) return d;
    d.setDate(d.getDate() - 1);
  }
  return d; // fallback (should never happen)
}

/** Format a Date as "YYYY-MM-DD". */
export function toDateStr(date) {
  return toKey(date);
}
